import { REDACTED, normalizeKey } from './redact.js';
import { createSecretNameMatcher, redactUrlsIn } from './url.js';
/**
 * Taking credentials out of free text.
 *
 * There is one rule, and it is worth stating exactly because the boundary is
 * where the value of this lies:
 *
 * > **Anything *labelled* as a credential is redacted, wherever it appears.**
 * > An unlabelled one is not.
 *
 * Labelled covers all three shapes a credential actually turns up in inside an
 * error message or a stack:
 *
 *   - a URL parameter — `https://api/x?token=…`
 *   - a `name=value` or `name: value` pair — `token=…`, `Authorization: …`
 *   - an auth scheme followed by its credential — `Bearer …`
 *
 * The first was handled before the other two, which was an accident of where
 * the code happened to look rather than a policy: the *same* name list decides
 * all three, so `?token=` and a bare `token=` in the next sentence now get the
 * same treatment.
 *
 * What is deliberately *not* attempted is spotting a bare high-entropy string —
 * `ghp_abc123…` on its own, with nothing naming it. That needs either a
 * catalogue of every vendor's token format, which rots, or an entropy heuristic,
 * which redacts request ids and base64 payloads and teaches people to distrust
 * the output. Either one would trade a real, explicable guarantee for a vague
 * one. Sanitise before throwing, or pass your own `scrubText`.
 */
/**
 * Schemes whose credential is the next token, per the HTTP `Authorization`
 * grammar plus the two AWS ones that show up in SDK errors.
 */
export const AUTH_SCHEMES = [
    'bearer',
    'basic',
    'digest',
    'token',
    'apikey',
    'negotiate',
    'aws4-hmac-sha256',
];
/** How long an unpunctuated word must be before it is taken for a credential. */
const CREDENTIAL_MIN_LENGTH = 16;
const AUTH_SCHEME_PATTERN = new RegExp(`\\b(${AUTH_SCHEMES.map((scheme) => scheme.replace(/[-]/g, '\\-')).join('|')})\\s+([^\\s,;]+)`, 'gi');
/**
 * A `name=value` or `name: value` pair.
 *
 * Both halves may be quoted, because the commonest way a header bag reaches an
 * error message is as JSON: `{"apiKey": "…"}`. The separator is captured rather
 * than reconstructed, so whatever punctuation sat between the name and the value
 * — a closing quote, a colon, spaces — is put back exactly as it was found.
 */
const LABELLED_PAIR_PATTERN = /\b([A-Za-z][A-Za-z0-9_.-]{1,40})("?\s*[=:]\s*)(?:"([^"]*)"|'([^']*)'|([^\s,;&"'<>]+))/g;
/**
 * Whether what follows an auth scheme is a credential or an ordinary word.
 *
 * `Bearer ghp_secret` is one; `Bearer token expired` is a sentence. Punctuation
 * is the tell — real credentials carry `_`, `-`, `.`, `/`, `+`, `=` or digits —
 * with a length fallback for the rare all-letter token. Getting this wrong in
 * the cautious direction costs a word of diagnostics; getting it wrong the other
 * way costs a credential, so the test is deliberately generous.
 */
function looksLikeCredential(value) {
    return /[^A-Za-z]/.test(value) || value.length >= CREDENTIAL_MIN_LENGTH;
}
export function scrubSecretsIn(text, options = {}) {
    const placeholder = options.placeholder ?? REDACTED;
    const isSecret = createSecretNameMatcher(options);
    const alreadyDone = new Set([placeholder, encodeURIComponent(placeholder)]);
    // URLs first, so that by the time the pair pass runs, a URL's own parameters
    // are already dealt with and it has nothing left to find in them.
    let out = redactUrlsIn(text, options);
    // Then auth schemes, before pairs. `Authorization: Bearer ghp_secret` has to
    // lose the token that follows the scheme, and the pair pass alone would only
    // reach as far as the word `Bearer`.
    out = out.replace(AUTH_SCHEME_PATTERN, (match, scheme, credential) => {
        if (alreadyDone.has(credential))
            return match;
        return looksLikeCredential(credential) ? `${scheme} ${placeholder}` : match;
    });
    return scrubLabelledPairs(out, isSecret, placeholder, alreadyDone);
}
/**
 * Redact the values of pairs whose name labels a credential.
 *
 * Scanned by hand rather than with `String.replace`, for one reason that took a
 * failing test to notice: in `request rejected: token=ghp_secret`, a pattern
 * looking for `name<sep>value` matches `rejected: token=ghp_secret` *first* —
 * name `rejected`, value `token=ghp_secret`. That name is not a secret, so the
 * match is left alone, and because the regex has now consumed the rest of the
 * line the real pair is never examined at all. The credential survives
 * precisely because something harmless sat in front of it.
 *
 * So when a name turns out not to label a credential, the cursor is rewound to
 * just after that name, and what looked like its value gets its own turn at
 * being a name.
 */
function scrubLabelledPairs(text, isSecret, placeholder, alreadyDone) {
    const pattern = new RegExp(LABELLED_PAIR_PATTERN.source, 'g');
    let out = '';
    let cursor = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        const [full, name = '', separator = '', quoted, single, bare] = match;
        const value = quoted ?? single ?? bare ?? '';
        const skip = !isSecret(name) ||
            value === '' ||
            alreadyDone.has(value) ||
            // `Authorization: Bearer [redacted]` — the scheme pass has already taken
            // the credential, and the scheme name itself is not one. Replacing it
            // would only turn a readable line into two placeholders.
            AUTH_SCHEMES.includes(normalizeKey(value));
        if (skip) {
            pattern.lastIndex = match.index + name.length;
            continue;
        }
        const wrap = quoted !== undefined ? '"' : single !== undefined ? "'" : '';
        out += text.slice(cursor, match.index) + name + separator + wrap + placeholder + wrap;
        cursor = match.index + full.length;
    }
    return out + text.slice(cursor);
}
//# sourceMappingURL=scrub.js.map